import React, { useState, useMemo } from "react";
import styles from "./NewBusiness.module.css";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Tooltip, IconButton, CircularProgress } from "@mui/material";
import { COUNTRY_CODE_MAP } from "../../../constants/constant";
import { getDataWAuth } from "../../../utils/api-service";
import { DASHBOARD_API } from "../../../constants/dashboardConstants";

const NewBusiness = ({ data }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("current"); // 'current' or 'previous'
  const [downloadingItem, setDownloadingItem] = useState(null);

  // Get current and previous month names from data and convert to readable format
  const { currentMonthName, previousMonthName, currentFiscalMonth, previousFiscalMonth } = useMemo(() => {
    if (!data) {
      return { currentMonthName: '', previousMonthName: '', currentFiscalMonth: '', previousFiscalMonth: '' };
    }

    // Convert month number format (MM-yy) to month name format (MMM-yy)
    // e.g., "12-25" -> "Dec-25", "11-25" -> "Nov-25"
    const convertToMonthName = (monthStr) => {
      if (!monthStr) return '';
      const [month, year] = monthStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(month, 10) - 1; // Convert to 0-based index
      return `${monthNames[monthIndex]}-${year}`;
    };

    const currentName = convertToMonthName(data.currentMonthName || '');
    const previousName = convertToMonthName(data.previousMonthName || '');
    
    // Use the fiscal month years directly from the backend
    const currentFiscal = data.currentFiscalMonthYear || '';
    const previousFiscal = data.previousFiscalMonthYear || '';

    return { 
      currentMonthName: currentName, 
      previousMonthName: previousName,
      currentFiscalMonth: currentFiscal,
      previousFiscalMonth: previousFiscal
    };
  }, [data]);

  // Transform data for display
  const displayData = useMemo(() => {
    if (!data) return [];
    
    const monthData = selectedPeriod === "current" ? data.currentMonth : data.previousMonth;
    if (!monthData || !Array.isArray(monthData)) return [];

    // Map the array directly - each item has country, amount, and currency
    return monthData.map((item, index) => ({
      id: index + 1,
      country: item.country,
      amount: item.amount || 0,
      currency: item.currency
    }));
  }, [data, selectedPeriod]);

  const handleDownload = async (item) => {
    try {
      setDownloadingItem(item.id);
      
      const fiscalMonthYear = selectedPeriod === "current" ? currentFiscalMonth : previousFiscalMonth;
      const url = `${DASHBOARD_API.CCM_DASHBOARD}/newBusiness/export?country=${item.country}&fiscalMonthYear=${fiscalMonthYear}&currency=${item.currency}`;
      
      const response = await getDataWAuth(url);
      
      if (response.data && response.data.fileContent) {
        // Decode base64 and create blob
        const byteCharacters = atob(response.data.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Create download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = response.data.fileName || `NewBusiness_${item.country}_${fiscalMonthYear}_${item.currency}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download the file. Please try again.');
    } finally {
      setDownloadingItem(null);
    }
  };

  const handleToggle = (period) => {
    setSelectedPeriod(period);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>New Business</h3>
          <Tooltip 
            title="Figures include only extensions processed through the application and may differ from the New Business report"
            placement="top"
            arrow
          >
            <InfoOutlinedIcon className={styles.infoIcon} />
          </Tooltip>
        </div>
        <div className={styles.toggleContainer}>
          <button
            className={`${styles.toggleButton} ${selectedPeriod === "previous" ? styles.active : ""}`}
            onClick={() => handleToggle("previous")}
          >
            {previousMonthName}
          </button>
          <button
            className={`${styles.toggleButton} ${selectedPeriod === "current" ? styles.active : ""}`}
            onClick={() => handleToggle("current")}
          >
            {currentMonthName}
          </button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {displayData.length === 0 ? (
          <div className={styles.emptyState}>No new business data</div>
        ) : (
          <table className={styles.table}>
            <tbody>
              {displayData.map((item) => (
                <tr key={item.id} className={styles.tableRow}>
                  <td className={styles.countryCell}>
                    {COUNTRY_CODE_MAP[item.country] || item.country}
                  </td>
                  <td className={styles.amountCell}>
                    <div className={styles.amountWrapper}>
                      <span className={styles.amountText}>
                        {Number(item.amount).toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })} {item.currency || ''}
                      </span>
                      <Tooltip title="Download Details" placement="top" arrow>
                        <IconButton 
                          size="small" 
                          className={styles.downloadButton}
                          onClick={() => handleDownload(item)}
                          disabled={downloadingItem === item.id}
                        >
                          {downloadingItem === item.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <FileDownloadOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NewBusiness;
